/**
 * i18n Configuration
 *
 * Internationalization setup with react-i18next
 * Supports English (en) and Portuguese (pt) languages
 * Language detection is handled by useUserLanguage hook
 * Detection flow: localStorage -> Firestore -> browser -> default
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { logger } from '@/lib/logger';

// Import translation files
import enTranslations from '@/locales/en.json';
import ptTranslations from '@/locales/pt.json';

// Language resources
const resources = {
  en: {
    translation: enTranslations,
  },
  pt: {
    translation: ptTranslations,
  },
};

// Supported languages
export const SUPPORTED_LANGUAGES = ['en', 'pt'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// Default language
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

/**
 * Get initial language for i18n initialization
 * Matches the detection flow used by useUserLanguage hook
 * Flow: localStorage -> browser -> default
 */
const getInitialLanguage = (): SupportedLanguage => {
  try {
    // Check localStorage
    const stored = localStorage.getItem('i18nextLng');
    if (stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage)) {
      return stored as SupportedLanguage;
    }

    // Check browser language
    const browserLang = navigator.language || navigator.languages?.[0] || 'en';
    const langCode = browserLang.split('-')[0].toLowerCase();

    // Check if primary browser language is supported
    if (SUPPORTED_LANGUAGES.includes(langCode as SupportedLanguage)) {
      return langCode as SupportedLanguage;
    }

    // Check if any of the user's preferred languages are supported
    if (navigator.languages) {
      for (const lang of navigator.languages) {
        const code = lang.split('-')[0].toLowerCase();
        if (SUPPORTED_LANGUAGES.includes(code as SupportedLanguage)) {
          return code as SupportedLanguage;
        }
      }
    }
  } catch (error) {
    logger.warn('Could not detect initial language', { error });
  }

  // Fallback to default
  return DEFAULT_LANGUAGE;
};

// i18n configuration
const i18nConfig = {
  resources,
  lng: getInitialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,

  // Namespace configuration
  defaultNS: 'translation',
  ns: ['translation'],

  // Interpolation options
  interpolation: {
    escapeValue: false, // React already escapes values
  },

  // Development options
  debug: import.meta.env.DEV,

  // Save missing translations in development
  saveMissing: import.meta.env.DEV,
  missingKeyHandler: import.meta.env.DEV
    ? (lngs: readonly string[], ns: string, key: string): void => {
        logger.warn(`Missing translation: ${lngs.join(', ')}.${ns}.${key}`);
      }
    : undefined,

  // React integration
  react: {
    useSuspense: false, // Disable suspense for better error handling
  },
};

// Initialize i18n
i18n.use(initReactI18next).init(i18nConfig);

// Export configured i18n instance
export { i18n };

// Helper functions
export const getCurrentLanguage = (): SupportedLanguage => {
  return (i18n.language as SupportedLanguage) || DEFAULT_LANGUAGE;
};

export const isLanguageSupported = (language: string): language is SupportedLanguage => {
  return SUPPORTED_LANGUAGES.includes(language as SupportedLanguage);
};

export const changeLanguage = async (language: SupportedLanguage): Promise<void> => {
  if (!isLanguageSupported(language)) {
    throw new Error(`Unsupported language: ${language}`);
  }

  await i18n.changeLanguage(language);

  // Save to localStorage
  try {
    localStorage.setItem('i18nextLng', language);
  } catch (error) {
    logger.warn('Could not save language to localStorage', { error, language });
  }
};

// Language change event listener for external state management
export const onLanguageChange = (callback: (language: SupportedLanguage) => void): (() => void) => {
  const handleLanguageChange = (lng: string): void => {
    if (isLanguageSupported(lng)) {
      callback(lng);
    }
  };

  i18n.on('languageChanged', handleLanguageChange);

  // Return cleanup function
  return () => {
    i18n.off('languageChanged', handleLanguageChange);
  };
};
